import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  LockupDrillThroughDataTable,
  type LockupIdentityColumn,
} from "@/components/LockupDrillThroughDataTable";
import { LockupClientAssignmentTable } from "@/components/LockupClientAssignmentTable";
import { DrillThroughPageShell } from "@/components/DrillThroughPageShell";
import { MultiSelect } from "@/components/GenericFilterBar";
import { useToast } from "@/hooks/use-toast";
import { useDrillThroughTable } from "@/hooks/useDrillThroughTable";
import { useSidebarPersistence } from "@/hooks/useSidebarPersistence";
import { cn } from "@/lib/utils";
import { getDrillFiltersFromSearch } from "@/lib/drillThroughTableQuery";
import {
  formatCurrency,
  formatMonthLabel as formatDrillMonthLabel,
  getSelectedMonth as getDrillSelectedMonth,
  normalizeAgeBands as normalizeDrillAgeBands,
} from "@/lib/drillThroughTableNormalizers";
import type { SortState } from "@/lib/tableSort";
import {
  downloadLockupDrillThroughExcel,
  fetchLockupDrillThrough,
  fetchLockupFilterOptions,
  LOCKUP_DRILL_FILTER_KEYS,
  type LockupClientAssignmentRow,
  type LockupDrillThroughResponse,
  type LockupDrillThroughRow,
  type LockupFilters,
} from "@/analytics/lockup.api";

type ExportOption = "assignment_manager" | "client_assignment";

const LOADING_MESSAGE = "Loading Lockup drill-through...";

const MANAGER_IDENTITY_COLUMNS: LockupIdentityColumn[] = [
  { key: "assignment_manager", label: "Assignment Manager", sortKey: "name" },
];

const getManagerRows = (response: LockupDrillThroughResponse) => response.by_assignment_manager;
const getManagerTotal = (response: LockupDrillThroughResponse) => response.assignment_manager_total_rows;
const getClientRows = (response: LockupDrillThroughResponse) => response.by_client_assignment;
const getClientTotal = (response: LockupDrillThroughResponse) => response.client_assignment_total_rows;

export default function LockupDrillThroughTablesPage() {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const selectedMonth = getDrillSelectedMonth(searchParams);
  const searchKey = searchParams.toString();

  // Seeded once from the incoming URL (e.g. a chart drill-through link with a
  // service line already applied), then fully owned by the user from here on.
  const [serviceLines, setServiceLines] = useState<string[]>(() => searchParams.getAll("service_lines"));
  const [serviceLineOptions, setServiceLineOptions] = useState<string[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchLockupFilterOptions(undefined, controller.signal)
      .then((options) => setServiceLineOptions(options.service_lines))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const toggleServiceLine = (value: string) => {
    setServiceLines((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  };

  // Base filters only -- sort is owned independently per table below, never merged in here.
  const drillFilters = useMemo(
    () => ({
      ...getDrillFiltersFromSearch(searchParams, [...LOCKUP_DRILL_FILTER_KEYS]),
      service_lines: serviceLines,
    }),
    [searchParams, serviceLines],
  );

  const buildManagerFetchFilters = useCallback(
    (sort: SortState) => ({ ...drillFilters, managerSort: sort }),
    [drillFilters],
  );
  const buildClientFetchFilters = useCallback(
    (sort: SortState) => ({ ...drillFilters, clientSort: sort }),
    [drillFilters],
  );

  // Two fully independent fetch/sort lifecycles -- sorting one table never
  // touches the other's state, request, or loading indicator.
  const managerTable = useDrillThroughTable<LockupDrillThroughResponse, LockupDrillThroughRow>({
    selectedMonth,
    searchKey,
    drillFilters,
    getRows: getManagerRows,
    getTotal: getManagerTotal,
    fetchPage: fetchLockupDrillThrough,
    buildFetchFilters: buildManagerFetchFilters,
    errorMessage: "Unable to load Lockup drill-through data",
  });
  const clientTable = useDrillThroughTable<LockupDrillThroughResponse, LockupClientAssignmentRow>({
    selectedMonth,
    searchKey,
    drillFilters,
    getRows: getClientRows,
    getTotal: getClientTotal,
    fetchPage: fetchLockupDrillThrough,
    buildFetchFilters: buildClientFetchFilters,
    errorMessage: "Unable to load Lockup drill-through data",
  });

  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOption[]>([]);
  const [sidebarOpen, onSidebarOpenChange] = useSidebarPersistence();

  const handleExport = async () => {
    if (!selectedMonth || isExporting || exportOptions.length === 0) return;

    setIsExporting(true);
    try {
      await downloadLockupDrillThroughExcel(
        selectedMonth,
        drillFilters as Partial<LockupFilters>,
        exportOptions,
      );
    } catch (caught) {
      toast({
        variant: "destructive",
        title: "Couldn't export Lockup drill-through",
        description:
          caught instanceof Error ? caught.message : "Failed to export Excel file",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const toggleExportOption = (option: ExportOption) => {
    setExportOptions((current) =>
      current.includes(option)
        ? current.filter((value) => value !== option)
        : [...current, option],
    );
  };

  const response = managerTable.response ?? clientTable.response;

  const ageBands = useMemo(
    () => normalizeDrillAgeBands(response?.age_bands),
    [response],
  );

  const monthLabel =
    response?.monthLabel ||
    formatDrillMonthLabel(selectedMonth) ||
    "Selected month";

  return (
    <DrillThroughPageShell
      title="Lockup Drill Through"
      subtitle={monthLabel}
      backLabel="Back to Lockup"
      sidebarOpen={sidebarOpen}
      onSidebarOpenChange={onSidebarOpenChange}
      exportOptions={[
        { label: "Assignment Manager", value: "assignment_manager" },
        { label: "Client Assignment", value: "client_assignment" },
      ]}
      selectedExportOptions={exportOptions}
      onToggleExportOption={toggleExportOption}
      isExporting={isExporting}
      onExport={handleExport}
      error={!selectedMonth ? managerTable.error ?? clientTable.error : null}
      onRetry={() => {
        managerTable.reload();
        clientTable.reload();
      }}
      filterSlot={
        <MultiSelect
          label="Service Line"
          options={[...new Set([...serviceLineOptions, ...serviceLines])]}
          availableOptions={serviceLineOptions}
          selected={serviceLines}
          onToggle={toggleServiceLine}
        />
      }
    >
      {response && (
        <section
          className="grid gap-3 sm:grid-cols-3"
          aria-label="Lockup totals"
        >
          <SummaryCard label="WIP" value={response.total_wip_gbp} />
          <SummaryCard
            label="Aged Debt"
            value={response.total_aged_debt_gbp}
          />
          <SummaryCard
            label="Total Lockup"
            value={response.total_lockup_gbp}
            emphasized
          />
        </section>
      )}

      <section className="space-y-3">
        <LockupDrillThroughDataTable
          title="Assignment Manager / Lockup Summary"
          rows={managerTable.rows}
          ageBands={ageBands}
          identityColumns={MANAGER_IDENTITY_COLUMNS}
          loading={managerTable.loading}
          refetching={managerTable.refetching}
          loadingMessage={LOADING_MESSAGE}
          error={managerTable.error}
          totalRows={managerTable.totalRows}
          hasMore={managerTable.hasMore}
          loadingMore={managerTable.loadingMore}
          onLoadMore={managerTable.loadMore}
          sort={managerTable.sort}
          onSort={managerTable.onSort}
        />
        <LockupClientAssignmentTable
          title="Client Assignment / Lockup Summary"
          rows={clientTable.rows}
          ageBands={ageBands}
          loading={clientTable.loading}
          refetching={clientTable.refetching}
          loadingMessage={LOADING_MESSAGE}
          error={clientTable.error}
          totalRows={clientTable.totalRows}
          hasMore={clientTable.hasMore}
          loadingMore={clientTable.loadingMore}
          onLoadMore={clientTable.loadMore}
          sort={clientTable.sort}
          onSort={clientTable.onSort}
        />
      </section>
    </DrillThroughPageShell>
  );
}

function SummaryCard({ label, value, emphasized = false }: { label: string; value: number; emphasized?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-3 rounded-md border bg-card/60 px-4 py-2.5 shadow-sm backdrop-blur-sm",
        emphasized ? "border-primary/30 bg-primary/[0.04]" : "border-border/60",
      )}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums text-foreground">{formatCurrency(value, 0)}</p>
    </div>
  );
}
